import { format } from 'date-fns';
import { appointmentTypeLabel, meetingLine } from './appointmentActivityNotes';
import { buildThoughtlyActivityItems } from './thoughtlyActivity';

export const FEED_PAGE_SIZE = 25;
export const FEED_SOURCE_BUFFER = 5;
export const FEED_PREVIEW_CHARS = 150;

export const FEED_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'calls', label: 'Calls' },
  { id: 'messages', label: 'Messages' },
  { id: 'notes', label: 'Notes' },
  { id: 'appointments', label: 'Appointments' },
  { id: 'system', label: 'System' },
];

const AI_MIRROR_WINDOW_MS = 3 * 60 * 1000;
const SMS_DUPLICATE_WINDOW_MS = 2 * 60 * 1000;
const APPOINTMENT_LOG_WINDOW_MS = 5 * 60 * 1000;
const AI_APPOINTMENT_NOTE = 'Booked by Thoughtly AI Caller';

export const CALL_OUTCOME_LABELS = {
  answered: 'Answered',
  voicemail: 'Voicemail',
  machine: 'Voicemail',
  no_answer: 'No Answer',
  busy: 'Busy',
  callback_requested: 'Callback Requested',
  booked_meeting: 'Booked Meeting',
  closed_business: 'Closed Business',
};

export function formatCallOutcomeLabel(outcome) {
  if (!outcome) return '—';
  return CALL_OUTCOME_LABELS[outcome] ?? String(outcome).replace(/_/g, ' ');
}

const AI_OUTCOME_LABELS = {
  appointment_booked: 'Appointment Booked',
  qualified: 'Qualified',
  not_interested: 'Not Interested',
  no_answer: 'No Answer',
  voicemail: 'Voicemail',
};

const AI_STATUS_LABELS = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  failed: 'Failed',
};

export const ACTIVITY_LOG_TYPE_LABELS = {
  presentation: 'Presentation',
  app_submitted: 'App Submitted',
  referral: 'Referral Gathered',
  objection: 'Objection Received',
  manual_calls: 'Manual Calls',
  merge: 'Lead Merged',
  csv_import_merge: 'CSV Import Merge',
  event_registration: 'Event Registration',
  appointment_booked: 'Appointment Booked',
  appointment_rescheduled: 'Appointment Rescheduled',
  appointment_cancelled: 'Appointment Cancelled',
};

const ACTIVITY_LOG_CATEGORY = {
  manual_calls: 'calls',
  appointment_booked: 'appointments',
  appointment_rescheduled: 'appointments',
  appointment_cancelled: 'appointments',
  merge: 'system',
  csv_import_merge: 'system',
  event_registration: 'system',
  presentation: 'notes',
  app_submitted: 'notes',
  referral: 'notes',
  objection: 'notes',
};

const EMAIL_STATUS_LABELS = {
  sent: 'Sent',
  delivered: 'Delivered',
  opened: 'Opened',
  clicked: 'Clicked',
  bounced: 'Bounced',
  spam: 'Spam',
  unsubscribed: 'Unsubscribed',
  failed: 'Failed',
  sent_gmail: 'Sent via Gmail',
};

function toMs(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
}

function humanize(value) {
  return value ? String(value).replace(/_/g, ' ') : '';
}

export function truncatePlain(text, max = FEED_PREVIEW_CHARS) {
  const flat = String(text ?? '').replace(/\s+/g, ' ').trim();
  if (!flat) return '';
  return flat.length > max ? `${flat.slice(0, max).trimEnd()}…` : flat;
}

export function formatDurationShort(sec) {
  const total = Math.max(0, Math.floor(Number(sec) || 0));
  if (!total) return null;
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m === 0) return `${s}s`;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
}

/** Same pattern as the Next Appointment card: viewer's local time. */
export function formatFeedWhen(iso) {
  const ms = toMs(iso);
  return ms == null ? null : format(new Date(ms), "EEE, MMM d 'at' h:mm a");
}

export function formatFeedRelative(iso, now = Date.now()) {
  const ms = toMs(iso);
  if (ms == null) return '';
  const diff = Math.max(0, now - ms);
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const d = new Date(ms);
  return format(d, d.getFullYear() === new Date(now).getFullYear() ? 'MMM d' : 'MMM d, yyyy');
}

export function formatFeedFull(iso) {
  const ms = toMs(iso);
  return ms == null ? '' : format(new Date(ms), 'EEE, MMM d, yyyy h:mm a');
}

function detail(label, value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? { label, value: text } : null;
}

function compact(list) {
  return list.filter(Boolean);
}

/**
 * thoughtly-webhook writes a call_logs row for every finished AI call: same
 * agent (triggered_by), same duration, inserted right after the thoughtly_calls
 * update. Returns Map<call_logs.id, thoughtly_calls.id>.
 */
export function matchAiMirrorCallLogs(callLogs = [], thoughtlyCalls = []) {
  const matches = new Map();
  for (const call of thoughtlyCalls) {
    if (call.status !== 'completed' || !call.triggered_by) continue;
    const callMs = toMs(call.updated_at || call.created_at);
    if (callMs == null) continue;
    const duration = Number(call.duration_seconds) || 0;
    let best = null;
    let bestDelta = Infinity;
    for (const row of callLogs) {
      if (matches.has(row.id) || row.agent_id !== call.triggered_by) continue;
      if ((Number(row.duration_seconds) || 0) !== duration) continue;
      const rowMs = toMs(row.called_at);
      if (rowMs == null) continue;
      const delta = Math.abs(rowMs - callMs);
      if (delta <= AI_MIRROR_WINDOW_MS && delta < bestDelta) {
        best = row;
        bestDelta = delta;
      }
    }
    if (best) matches.set(best.id, call.id);
  }
  return matches;
}

/** Nearest unused candidate within the window, or null. */
function claimNearest(candidates, targetMs, windowMs, used) {
  let best = null;
  let bestDelta = Infinity;
  for (const c of candidates) {
    if (used.has(c.key)) continue;
    const delta = Math.abs(c.ms - targetMs);
    if (delta <= windowMs && delta < bestDelta) {
      best = c;
      bestDelta = delta;
    }
  }
  if (best) used.add(best.key);
  return best;
}

function callItem(row, resolveName) {
  const outcome = formatCallOutcomeLabel(row.outcome);
  const duration = formatDurationShort(row.duration_seconds);
  const label = row.direction === 'inbound' ? 'Inbound call' : 'Call';
  return {
    key: `call-${row.id}`,
    kind: 'call',
    at: row.called_at,
    categories: compact(['calls', row.notes?.trim() && 'notes']),
    title: [label, outcome, duration].filter(Boolean).join(' · '),
    actor: resolveName(row.agent_id || row.user_id, row.user),
    preview: truncatePlain(row.notes),
    details: compact([
      detail('Direction', humanize(row.direction)),
      detail('Outcome', outcome),
      detail('Duration', duration || '0s'),
    ]),
    body: row.notes?.trim() || null,
    bodyLabel: 'Notes',
    tab: 'calls',
    tabOptions: { callsFilter: 'manual' },
  };
}

function aiCallItem(call, resolveName, agentNames) {
  const outcome = call.outcome
    ? AI_OUTCOME_LABELS[call.outcome] || humanize(call.outcome)
    : AI_STATUS_LABELS[call.status] || humanize(call.status) || 'Pending';
  const duration = formatDurationShort(call.duration_seconds);
  return {
    key: `ai-call-${call.id}`,
    kind: 'ai_call',
    at: call.updated_at || call.created_at,
    categories: ['calls'],
    title: ['AI call', outcome, duration].filter(Boolean).join(' · '),
    actor: resolveName(call.triggered_by, call.triggered_by_user),
    preview: '',
    details: compact([
      detail('AI agent', agentNames[call.thoughtly_agent_id] || 'AI Agent'),
      detail('Status', AI_STATUS_LABELS[call.status] || humanize(call.status)),
      call.outcome ? detail('Outcome', outcome) : null,
      detail('Duration', duration),
    ]),
    transcript: call.transcript?.trim() || null,
    tab: 'calls',
    tabOptions: { callsFilter: 'ai' },
  };
}

function smsMessageItem(row, resolveName) {
  const inbound = row.direction === 'inbound';
  return {
    key: `sms-${row.id}`,
    kind: 'sms',
    at: row.created_at,
    categories: ['messages'],
    title: inbound ? 'SMS received' : 'SMS sent',
    actor: inbound ? null : resolveName(row.agent_id),
    preview: truncatePlain(row.body),
    details: [],
    body: row.body?.trim() || null,
    tab: 'sms',
  };
}

function legacySmsItem(row, resolveName) {
  return {
    key: `sms-log-${row.id}`,
    kind: 'sms',
    at: row.created_at,
    categories: ['messages'],
    title: 'SMS sent',
    actor: resolveName(row.agent_id, row.user),
    preview: truncatePlain(row.message),
    details: compact([
      detail('Provider', row.provider === 'telnyx' ? 'Telnyx' : 'Twilio'),
      detail('To', row.to_phone),
    ]),
    body: row.message?.trim() || null,
    tab: 'sms',
  };
}

function emailItem(row, resolveName, toPlainText) {
  const inbound = row.direction === 'inbound';
  const text = toPlainText(row.body_html || '');
  const status = row.status || 'sent';
  return {
    key: `email-${row.id}`,
    kind: 'email',
    at: row.created_at,
    categories: ['messages'],
    title: inbound ? 'Email received' : 'Email sent',
    actor: inbound ? null : resolveName(row.agent_id, row.user),
    subtitle: row.subject || '(no subject)',
    preview: truncatePlain(text),
    details: compact([
      detail('Subject', row.subject || '(no subject)'),
      detail('Status', EMAIL_STATUS_LABELS[status] || humanize(status)),
      detail('To', row.to_email),
      row.open_count > 0 ? detail('Opens', row.open_count) : null,
      row.click_count > 0 ? detail('Clicks', row.click_count) : null,
    ]),
    body: text || null,
    tab: 'email',
  };
}

function appointmentDetails(appt, resolveName, bookedBy) {
  const when = formatFeedWhen(appt.scheduled_at);
  const duration = Number(appt.duration_minutes) || null;
  const guests = Array.isArray(appt.guests)
    ? appt.guests.map((g) => g?.name || g?.email).filter(Boolean).join(', ')
    : '';
  return compact([
    detail('Title', appt.title),
    detail('Type', appointmentTypeLabel(appt.appointment_type)),
    detail('When', when && duration ? `${when} (${duration} min)` : when),
    detail('Status', humanize(appt.status)),
    detail('Meeting', meetingLine(appt.meeting_type, appt.meeting_location)?.replace(/^Meeting:\s*/, '')),
    detail('Closer', resolveName(appt.assigned_to, appt.assignee)),
    detail('Booked by', bookedBy),
    detail('Guests', guests),
  ]);
}

function appointmentSubtitle(appt) {
  return [appointmentTypeLabel(appt.appointment_type), formatFeedWhen(appt.scheduled_at)]
    .filter(Boolean)
    .join(' · ');
}

function activityLogItem(row, resolveName) {
  const type = row.activity_type;
  const label = ACTIVITY_LOG_TYPE_LABELS[type] || humanize(type || 'Activity');
  const amount = row.amount_dollars != null && row.amount_dollars !== ''
    ? `$${Number(row.amount_dollars).toLocaleString()}`
    : null;
  const summary =
    row.notes ||
    row.objection_text ||
    row.referral_name ||
    [row.policy_type, amount].filter(Boolean).join(' · ');
  const category = ACTIVITY_LOG_CATEGORY[type] || 'system';
  return {
    key: `activity-${row.id}`,
    kind: 'activity',
    activityType: type,
    at: row.created_at,
    categories: compact([category, category !== 'notes' && row.notes?.trim() && 'notes']),
    title: label,
    actor: resolveName(row.agent_id, row.user),
    preview: truncatePlain(summary),
    details: compact([
      detail('Date', row.activity_date),
      detail('Policy type', row.policy_type),
      detail('Amount', amount),
      detail('Referral', row.referral_name),
      detail('Referral phone', row.referral_phone),
      detail('Objection', row.objection_text),
      detail('How handled', row.how_handled),
      detail('Calls', row.call_count),
      detail('Call outcome', row.call_outcome ? formatCallOutcomeLabel(row.call_outcome) : null),
    ]),
    body: row.notes?.trim() || null,
    bodyLabel: 'Notes',
  };
}

function reminderItems(row, resolveName) {
  const items = [];
  const shared = {
    kind: 'reminder',
    categories: ['system'],
    subtitle: row.title,
    details: compact([
      detail('Reminder', row.title),
      detail('Due', formatFeedWhen(row.due_at)),
      detail('Assigned to', resolveName(row.assigned_to, row.assignee)),
      detail('Created by', resolveName(row.created_by, row.creator)),
      detail('Status', humanize(row.status)),
    ]),
    body: row.notes?.trim() || null,
    bodyLabel: 'Notes',
    tab: 'reminders',
  };
  items.push({
    ...shared,
    key: `reminder-set-${row.id}`,
    at: row.created_at,
    title: `Reminder set for ${formatFeedWhen(row.due_at) || 'unspecified time'}`,
    actor: resolveName(row.created_by, row.creator),
    preview: truncatePlain(row.notes),
  });
  if (row.status === 'completed' && row.completed_at) {
    items.push({
      ...shared,
      key: `reminder-done-${row.id}`,
      at: row.completed_at,
      title: 'Reminder completed',
      actor: null,
      preview: '',
    });
  } else if (row.status === 'dismissed' && row.updated_at) {
    items.push({
      ...shared,
      key: `reminder-dismissed-${row.id}`,
      at: row.updated_at,
      title: 'Reminder dismissed',
      actor: null,
      preview: '',
    });
  }
  return items;
}

const ENROLLMENT_STATUS_LABELS = { active: 'Active', completed: 'Completed', cancelled: 'Stopped' };

function enrollmentItem(row) {
  const status = ENROLLMENT_STATUS_LABELS[row.status] || humanize(row.status);
  return {
    key: `sequence-${row.id}`,
    kind: 'sequence',
    at: row.enrolled_at,
    categories: ['system'],
    title: `Enrolled in ${row.sequence_name || 'sequence'}`,
    actor: row.enrolled_by ? row.enrolled_by_name || 'User' : null,
    subtitle: [row.enrolled_by ? null : 'Automatic', `Now ${status.toLowerCase()}`]
      .filter(Boolean)
      .join(' · '),
    preview: '',
    details: compact([
      detail('Sequence', row.sequence_name),
      detail('Status', status),
      detail('Progress', row.current_step_label),
      row.status === 'active' ? detail('Next send', formatFeedWhen(row.next_send_at)) : null,
    ]),
    tab: 'sequences',
  };
}

function sessionItem(row, resolveName) {
  const startMs = toMs(row.created_at);
  const endMs = toMs(row.updated_at);
  const seconds = startMs != null && endMs != null ? Math.round((endMs - startMs) / 1000) : 0;
  const duration = formatDurationShort(seconds) || '<1m';
  const intake = row.intake_data || {};
  const coverage = row.coverage_calculation || {};
  const path = (row.branches_taken || []).map((b) => b.branchLabel || b.stepId).filter(Boolean).join(' → ');
  return {
    key: `session-${row.id}`,
    kind: 'session',
    at: row.created_at,
    categories: ['system'],
    title: `Workspace session · ${duration}`,
    actor: resolveName(row.agent_id),
    subtitle: row.script_type === 'fex' ? 'FEX script' : 'Ethos script',
    preview: '',
    details: compact([
      detail('Path', path),
      detail('Beneficiary', intake.beneficiary),
      detail('Coverage', intake.coverageGoal || intake.fexCoverageAmount),
      coverage.totalInsurableNeed > 0
        ? detail('Need', `$${Math.round(coverage.totalInsurableNeed).toLocaleString()}`)
        : null,
    ]),
    tab: 'sessions',
  };
}

function leadNoteItem(lead) {
  const text = lead?.notes?.trim();
  if (!text) return null;
  return {
    key: `lead-note-${lead.id}`,
    kind: 'note',
    at: lead.created_at,
    categories: ['notes'],
    title: 'Initial note',
    actor: null,
    preview: truncatePlain(text),
    details: [],
    body: text,
    tab: 'notes',
  };
}

/**
 * Builds the merged, newest-first feed.
 *
 * Duplicate sources are collapsed:
 * - sms_logs rows that have an outbound sms_messages twin (same body, ~same time).
 * - call_logs rows written by thoughtly-webhook for an AI call.
 * - activity_logs appointment_booked / appointment_cancelled rows that describe
 *   an appointments row we already render (the log's agent becomes the actor).
 * - The AI "appointment booked" item when the webhook's appointments row exists.
 */
export function buildLeadFeed({
  lead,
  appointments = [],
  thoughtlyCalls = [],
  thoughtlyAgents = [],
  smsMessages = [],
  emailLogs = [],
  reminders = [],
  enrollments = [],
  sessions = [],
  resolveName = () => null,
  toPlainText = (s) => s,
}) {
  const activity = lead?.activity ?? [];
  const callLogs = activity.filter((a) => a.type === 'call');
  const smsLogs = activity.filter((a) => a.type === 'sms');
  const activityLogs = activity.filter((a) => a.type === 'activity');
  const items = [];

  const agentNames = {};
  for (const a of thoughtlyAgents) {
    if (a.thoughtly_agent_id) agentNames[a.thoughtly_agent_id] = a.name;
  }

  const aiMirrors = matchAiMirrorCallLogs(callLogs, thoughtlyCalls);
  for (const row of callLogs) {
    if (!aiMirrors.has(row.id)) items.push(callItem(row, resolveName));
  }
  for (const call of thoughtlyCalls) items.push(aiCallItem(call, resolveName, agentNames));

  const outboundSms = smsMessages
    .filter((m) => m.direction === 'outbound')
    .map((m) => ({ key: m.id, ms: toMs(m.created_at), body: (m.body || '').trim() }))
    .filter((m) => m.ms != null);
  const usedSms = new Set();
  for (const row of smsLogs) {
    const ms = toMs(row.created_at);
    const body = (row.message || '').trim();
    const twin = ms != null
      ? claimNearest(outboundSms.filter((m) => m.body === body), ms, SMS_DUPLICATE_WINDOW_MS, usedSms)
      : null;
    if (!twin) items.push(legacySmsItem(row, resolveName));
  }
  for (const row of smsMessages) items.push(smsMessageItem(row, resolveName));

  for (const row of emailLogs) items.push(emailItem(row, resolveName, toPlainText));

  const logCandidates = (type) => activityLogs
    .filter((r) => r.activity_type === type)
    .map((r) => ({ key: r.id, ms: toMs(r.created_at), row: r }))
    .filter((c) => c.ms != null);
  const bookedLogs = logCandidates('appointment_booked');
  const cancelledLogs = logCandidates('appointment_cancelled');
  const usedLogs = new Set();

  const aiAppointmentItems = buildThoughtlyActivityItems(thoughtlyCalls).filter(
    (i) => i.subtype === 'appointment',
  );
  const aiApptCandidates = aiAppointmentItems
    .map((i) => ({ key: i.id, ms: toMs(i.activity_at), item: i }))
    .filter((c) => c.ms != null);
  const usedAiAppts = new Set();

  for (const appt of appointments) {
    const createdMs = toMs(appt.created_at);
    const isAiBooked = appt.notes === AI_APPOINTMENT_NOTE;
    if (isAiBooked && createdMs != null) {
      claimNearest(aiApptCandidates, createdMs, APPOINTMENT_LOG_WINDOW_MS, usedAiAppts);
    }
    const bookedLog = createdMs != null
      ? claimNearest(bookedLogs, createdMs, APPOINTMENT_LOG_WINDOW_MS, usedLogs)
      : null;
    const bookedBy = isAiBooked
      ? 'AI Caller'
      : resolveName(appt.created_by) || (bookedLog ? resolveName(bookedLog.row.agent_id, bookedLog.row.user) : null);
    const details = appointmentDetails(appt, resolveName, bookedBy);
    const subtitle = appointmentSubtitle(appt);
    const body = appt.notes?.trim() && !isAiBooked ? appt.notes.trim() : null;

    items.push({
      key: `appointment-booked-${appt.id}`,
      kind: 'appointment',
      at: appt.created_at,
      categories: ['appointments'],
      title: 'Appointment booked',
      actor: bookedBy,
      subtitle,
      preview: '',
      details,
      body,
      bodyLabel: 'Notes',
    });

    if (appt.status === 'cancelled') {
      const cancelledMs = toMs(appt.updated_at);
      const cancelLog = cancelledMs != null
        ? claimNearest(cancelledLogs, cancelledMs, APPOINTMENT_LOG_WINDOW_MS, usedLogs)
        : null;
      items.push({
        key: `appointment-cancelled-${appt.id}`,
        kind: 'appointment',
        cancelled: true,
        at: appt.updated_at || appt.created_at,
        categories: ['appointments'],
        title: 'Appointment cancelled',
        actor: cancelLog ? resolveName(cancelLog.row.agent_id, cancelLog.row.user) : null,
        subtitle,
        preview: '',
        details,
        body: cancelLog?.row.notes?.trim() || null,
        bodyLabel: 'Notes',
      });
    }
  }

  for (const c of aiApptCandidates) {
    if (usedAiAppts.has(c.key)) continue;
    items.push({
      key: c.key,
      kind: 'appointment',
      at: c.item.activity_at,
      categories: ['appointments'],
      title: 'Appointment booked',
      actor: 'AI Caller',
      subtitle: c.item.scheduledLabel !== '—' ? c.item.scheduledLabel : null,
      preview: '',
      details: [],
      tab: 'calls',
      tabOptions: { callsFilter: 'ai' },
    });
  }

  for (const row of activityLogs) {
    if (usedLogs.has(row.id)) continue;
    items.push(activityLogItem(row, resolveName));
  }

  for (const row of reminders) items.push(...reminderItems(row, resolveName));
  for (const row of enrollments) items.push(enrollmentItem(row));
  for (const row of sessions) items.push(sessionItem(row, resolveName));

  const note = leadNoteItem(lead);
  if (note) items.push(note);

  return items
    .filter((i) => toMs(i.at) != null)
    .sort((a, b) => toMs(b.at) - toMs(a.at));
}

/**
 * A source that came back with as many rows as requested may have older rows
 * we did not fetch. Items older than the newest such cut-off could be missing
 * neighbours, so they are held back until the next "Load more".
 * Each entry: { rows, limit, field }.
 */
export function computeFeedHorizon(sources) {
  let horizon = null;
  for (const { rows, limit, field } of sources) {
    if (!limit || !Array.isArray(rows) || rows.length < limit) continue;
    let oldest = null;
    for (const r of rows) {
      const ms = toMs(r[field]);
      if (ms != null && (oldest == null || ms < oldest)) oldest = ms;
    }
    if (oldest != null && (horizon == null || oldest > horizon)) horizon = oldest;
  }
  return horizon;
}

export function feedItemMatchesFilter(item, filter) {
  return filter === 'all' || item.categories.includes(filter);
}

export function isFeedItemAtOrAfter(item, horizon) {
  return horizon == null || toMs(item.at) >= horizon;
}
